// no-dd-sa:javascript-code-style/radix
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Autocomplete, Box, Button, Grid, Link, TextField } from '@mui/material';
import { useRecoilValue } from 'recoil';
import moment from 'moment';
import { find, get } from 'lodash';

import { selectorClientsActive } from '../../atoms/clients';
import AxlTableContainer from '../../components/AxlTableContainer';
import AxlAutocomplete from '../../components/AxlAutocomplete';
import { getRegionList, listSftpJobs } from '../../repositories/HttpRepository';
import AxlSelect from '../../components/AxlSelect';
import AxlDateTimePicker from '../../components/AxlDateTimePicker';
import { getStatusColor } from '../../constants/common';
import { getFileName } from '../../utils/csvUtil';

const STATUSES = [
  { label: 'All status', value: '' },
  { label: 'PENDING', value: 'PENDING' },
  { label: 'DONE', value: 'DONE' },
  { label: 'ERROR', value: 'ERROR' },
  { label: 'DUPLICATED', value: 'DUPLICATED' },
];

const DEFAULT_FILTERS = {
  from: null,
  to: null,
  client_ids: null,
  statuses: null,
  page: 1,
  size: 20,
  order_by: 'ts',
  desc: true,
};

const renderLanesInput = (params) => <TextField {...params} label="Lanes" />;

function SftpJobManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sftpJobs, setSftpJobs] = useState({});
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [regions, setRegions] = useState([]);
  const activeClients = useRecoilValue(selectorClientsActive);
  const queryParams = new URLSearchParams(location.search);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const updateFilter = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  const updateFilters = (updated) => {
    setFilters({ ...filters, ...updated });
  };

  useEffect(() => {
    // no-dd-sa:javascript-code-style/assignment-name
    const client_ids = queryParams
      .get('client_ids')
      ?.split(',')
      .map((id) => parseInt(id));
    const statuses = queryParams.get('statuses')?.split(',');
    const lanes = queryParams.get('lanes')?.split(',');
    const from = queryParams.get('from') ? parseInt(queryParams.get('from')) : null;
    const to = queryParams.get('to') ? parseInt(queryParams.get('to')) : null;
    const page = queryParams.get('page') ? parseInt(queryParams.get('page')) : 1;
    const updatedFilters = { client_ids, statuses, lanes, from, to, page };
    setFilters({ ...filters, ...updatedFilters });

    getRegionList().then((res) => {
      if (!res.ok) return;

      const regions = res.data
        .map((item) => item.properties)
        .map((region) => ({
          label: `[${region.code}] ${region.display_name}`,
          value: region.code,
        }));
      setRegions(regions);
    });
  }, []);

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      return;
    }

    const params = { ...filters, page: filters.page - 1 };
    setLoading(true);
    listSftpJobs(params).then((res) => {
      if (res.ok) {
        setSftpJobs(res.data);
      } else {
        setSftpJobs([]);
      }
      setLoading(false);
      updateQuery();
    });
  }, [filters]);

  const updateQuery = () => {
    let filterParams = Object.keys(filters)
      .filter((k) => !!filters[k])
      .reduce((a, k) => ({ ...a, [k]: filters[k] }), {});

    delete filterParams['desc'];
    delete filterParams['order_by'];
    delete filterParams['size'];

    navigate(
      {
        search: '?' + new URLSearchParams(filterParams).toString(),
      },
      { replace: true },
    );
  };

  const viewDetail = (job) => {
    if (!job) return;
    navigate(`/sftp-jobs/${job.id}`);
  };

  const onPageChange = (page) => {
    if (page !== filters.page) {
      updateFilter('page', page);
    }
  };

  const handleChangeDate = (e) => {
    const date = e.target.value;
    if (!date) {
      updateFilters({
        from: undefined,
        to: undefined,
        page: DEFAULT_FILTERS.page,
      });
    } else {
      const selectedDate = moment(date);
      const from = selectedDate.startOf('day').valueOf();
      const to = selectedDate.endOf('day').valueOf();
      updateFilters({ from, to, page: DEFAULT_FILTERS.page });
    }
  };

  const changeClients = (values) => {
    const clientIds = values.map((c) => c.client_id);
    updateFilters({
      client_ids: !clientIds.length ? null : clientIds,
      page: DEFAULT_FILTERS.page,
    });
  };

  const handleChangeStatus = (e) => {
    const { value } = e.target;
    updateFilters({
      statuses: !value ? undefined : [value],
      page: DEFAULT_FILTERS.page,
    });
  };

  const handleChangeLanes = (data) => {
    const lanes = data.map((dt) => dt.value);
    updateFilters({
      lanes: !lanes.length ? undefined : lanes,
      page: DEFAULT_FILTERS.page,
    });
  };

  const renderClients = (ids) => {
    if (!ids) {
      return null;
    }

    return (
      <Box>
        {ids.map((cid) => (
          <Box key={cid} sx={{ whiteSpace: 'nowrap' }}>
            [{cid}] {get(find(activeClients, ['client_id', cid]), 'name', cid)}
          </Box>
        ))}
      </Box>
    );
  };

  const fields = [
    {
      fieldName: 'uploader',
      label: 'Uploader',
      isDisableSort: true,
      renderer: (v, i) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          [{get(i, 'client_id')}] {v}
        </Box>
      ),
    },
    {
      fieldName: 'client_ids',
      label: 'Clients',
      isDisableSort: true,
      renderer: (v) => renderClients(v),
    },
    {
      fieldName: 'action',
      label: 'Action',
      isDisableSort: true,
      renderer: (v) => <Box sx={{ color: v === 'CANCEL' && 'red' }}>{v}</Box>,
    },
    {
      fieldName: 'lanes',
      label: 'Lanes',
      isDisableSort: true,
      renderer: (v) => v?.map((v) => <div key={v}>{v}</div>),
    },
    {
      fieldName: 'lane_codes',
      label: 'Lane Codes',
      isDisableSort: true,
      renderer: (v) => v?.map((v) => <div key={v}>{v}</div>),
    },
    {
      fieldName: 'path',
      label: 'File Name',
      isDisableSort: true,
      renderer: (v, i) => (
        <Link onClick={() => viewDetail(i)} style={{ cursor: 'pointer' }}>
          {getFileName(v)}
        </Link>
      ),
    },
    {
      fieldName: 'ts',
      label: 'Uploaded Time',
      isDisableSort: true,
      renderer: (v) => (!v ? 'N/A' : moment(v).format('YYYY-MM-DD HH:mm')),
    },
    {
      fieldName: 'status',
      label: 'Status',
      isDisableSort: true,
      renderer: (v) => <Box sx={{ color: getStatusColor(v), fontWeight: 700 }}>{v}</Box>,
    },
    {
      fieldName: 'total_records',
      label: 'No. of Shipments',
      isDisableSort: true,
    },
    { fieldName: 'unique_count', label: 'Unique count', isDisableSort: true },
    {
      fieldName: 'duplicate_count',
      label: 'Duplicate count',
      isDisableSort: true,
    },
  ];

  const selectedClients = activeClients?.filter((c) => filters?.client_ids?.includes(c.client_id));
  const selectedStatus = filters?.statuses?.slice().pop() || '';
  const selectedDate = !filters?.from ? null : moment(filters?.from);
  const selectedLanes = regions?.filter((r) => filters?.lanes?.includes(r.value));

  return (
    <Box sx={{ p: 3 }}>
      <Box>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid
            size={{
              sm: 2,
              xs: 12,
            }}
          >
            <AxlDateTimePicker dateOnly label="Submitted Date" onChange={handleChangeDate} format="MM/DD/YYYY" value={selectedDate} variant="outlined" inputVariant="outlined" size="small" />
          </Grid>
          <Grid
            size={{
              sm: 3,
              xs: 12,
            }}
          >
            <AxlAutocomplete
              isMultiple={true}
              options={activeClients}
              handleChange={(_, selected) => changeClients(selected)}
              value={selectedClients}
              getOptionLabel={(option) => `[${option?.client_id}] ${option?.name}`}
              placeholder="Select clients..."
            />
          </Grid>
          <Grid
            size={{
              sm: 2,
              xs: 12,
            }}
          >
            <AxlSelect value={selectedStatus} options={STATUSES} onChange={handleChangeStatus} placeholder="Status" />
          </Grid>
          <Grid
            size={{
              sm: 2,
              xs: 12,
            }}
          >
            <AxlAutocomplete isMultiple={true} size="small" options={regions} value={selectedLanes} handleChange={(_, selected) => handleChangeLanes(selected)} renderInput={renderLanesInput} />
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: 1 }}>
        <AxlTableContainer fields={fields} result={sftpJobs} filters={filters} isLoading={loading} tableStyles={{ maxHeight: 'none' }} onPageChange={onPageChange} pagination={true} />
      </Box>
    </Box>
  );
}

export default SftpJobManagement;
